import { ComponentProps, forwardRef } from 'react';
import {
  BaseProps,
  ComponentSize,
  ComponentShadow,
} from 'src/modules/shared/types/component.type';
import {
  baseInputClasses,
  inputVariantClasses,
  inputSizeClasses,
  getDisabledClasses,
  getErrorClasses,
} from 'src/modules/shared/styles/component.styles';
import InputError from '../input-error';

interface NumberInputProps
  extends BaseProps<
    Omit<ComponentProps<'input'>, 'size' | 'variant' | 'type'>
  > {
  min?: number;
  max?: number;
  step?: number;
}

const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      className = '',
      variant = 'primary',
      size = 'md',
      disabled = false,
      error,
      shadow,
      min,
      max,
      step,
      ...rest
    },
    ref
  ) => {
    const shadowMap: Record<ComponentShadow, string> = {
      'inset-sm': 'shadow-sm',
      inset: 'shadow-md',
      flat: 'shadow-md',
      none: '',
      sm: 'shadow-sm',
      md: 'shadow-md',
      lg: 'shadow-lg',
      xl: 'shadow-xl',
      '2xl': 'shadow-2xl',
      inner: 'shadow-inner',
    };
    const shadowClasses = shadow ? `${shadowMap[shadow]}` : '';

    const classes = [
      ...baseInputClasses,
      inputVariantClasses[variant],
      inputSizeClasses[size as ComponentSize],
      getDisabledClasses(disabled),
      getErrorClasses(!!error),
      'border',
      shadowClasses,
      className,
    ].join(' ');

    return (
      <div className="flex flex-1 flex-col w-full">
        <input
          ref={ref}
          type="number"
          className={classes}
          disabled={disabled}
          min={min}
          max={max}
          step={step}
          {...rest}
        />
        <InputError message={error} />
      </div>
    );
  }
);

NumberInput.displayName = 'NumberInput';

export default NumberInput;
